import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { isEmpty } from 'lodash';
import {
  Observable,
  Subscription,
  concatMap,
  forkJoin,
  from,
  interval,
  of,
  switchMap,
  takeUntil,
  timer
} from 'rxjs';
import { InsuranceCertificateForm } from 'src/app/classes/form-groups/insurance-certificate-form';
import { InsuranceComplianceResultToLabelMap } from 'src/app/classes/insurance-compliance-result-to-label-map';
import { LoadingMask } from 'src/app/classes/loading-mask';
import {
  CanadianWorkersCompensation,
  Company,
  CompanyVendor,
  InsuranceCertificate,
  InsurancePolicy,
  Vendor,
  ViveProgress
} from 'src/app/classes/models';
import { ComplianceSetting } from 'src/app/classes/models/compliance-setting';
import { InsuranceAgent } from 'src/app/classes/models/insurance-agent';
import { ComplianceReviewAction } from 'src/app/interfaces/compliance-review-action';
import { ComplianceResultStatuses } from 'src/app/interfaces/compliance/compliance-result-statuses';
import { AUTOInsuranceComplianceResults } from 'src/app/interfaces/compliance/results/insurance/auto-insurance-compliance-results';
import { GLInsuranceComplianceResults } from 'src/app/interfaces/compliance/results/insurance/gl-insurance-compliance-results';
import { PLInsuranceComplianceResults } from 'src/app/interfaces/compliance/results/insurance/pl-insurance-compliance-results';
import { UMBInsuranceComplianceResults } from 'src/app/interfaces/compliance/results/insurance/umb-insurance-compliance-results';
import { WCInsuranceComplianceResults } from 'src/app/interfaces/compliance/results/insurance/wc-insurance-compliance-results';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { InsuranceCertificateStatus } from 'src/app/interfaces/insurance-certificate-status';
import { State } from 'src/app/interfaces/state';
import { AddEditInsuranceAgentModalComponent } from 'src/app/modals/add-edit-insurance-agent-modal/add-edit-insurance-agent-modal.component';
import { CoiUploadModalComponent } from 'src/app/modals/coi-upload-modal/coi-upload-modal.component';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { CreateTicketModalComponent } from 'src/app/modals/create-ticket-modal/create-ticket-modal.component';
import { AdminService } from 'src/app/services/admin.service';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { ComplianceStepStatusService } from 'src/app/services/compliance-step-status.service';
import { ComplianceService } from 'src/app/services/compliance.service';
import { InsuranceExpirationService } from 'src/app/services/insurance-expiration.service';
import { InsuranceService } from 'src/app/services/insurance.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

declare type InsuranceComplianceResult =
  | GLInsuranceComplianceResults
  | AUTOInsuranceComplianceResults
  | UMBInsuranceComplianceResults
  | WCInsuranceComplianceResults
  | PLInsuranceComplianceResults;

@Component({
  selector: 'app-compliance-insurance-review',
  templateUrl: './compliance-insurance-review.component.html',
  styleUrls: ['./compliance-insurance-review.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ComplianceInsuranceReviewComponent implements OnInit {
  constructor(
    private ToastService: ToastService,
    private SharedDataService: SharedDataService,
    private ComplianceService: ComplianceService,
    private CompanyVendorService: CompanyVendorService,
    private UtilityService: UtilityService,
    private ModalService: NgbModal,
    private route: ActivatedRoute,
    private InsuranceService: InsuranceService,
    private router: Router,
    private FormBuilder: FormBuilder,
    private AuthService: AuthService,
    private AdminService: AdminService,
    private ComplianceStepStatusService: ComplianceStepStatusService,
    private InsuranceExpirationService: InsuranceExpirationService,
    private HostElement: ElementRef
  ) {
    this.COIGridFormGroup.get('showArchived')?.valueChanges.subscribe({
      next: (changes) => {
        if (changes) {
          Object.assign(this.SuppliedCOIGridQuery, {
            status: {
              $in: ['Received', 'Reviewing', 'Accepted', 'Rejected', 'Archived']
            }
          });
        } else {
          Object.assign(this.SuppliedCOIGridQuery, {
            status: { $notIn: ['Archived', 'Replaced'] }
          });
        }

        this.reloadCOIGrid = true;
      }
    });

    this.SharedDataServiceSubscription = this.SharedDataService.listenForEvent<
      ViveProgress | null | ComplianceReviewAction | Set<State>
    >().subscribe({
      next: ({ eventName, data }) => {
        if (eventName === 'SetSharedOperationStates' && data instanceof Set) {
          this.SharedOperationStates = data;
        }

        if (eventName === 'SetReviewAction' && typeof data === 'string') {
          this.action = data;
          this.SharedDataService.sendEvent(
            'GetComplianceProgress',
            new ViveProgress({ type: 'Insurance' })
          );
        }

        if (
          eventName === 'SetComplianceProgress' &&
          (data instanceof ViveProgress || data === null)
        ) {
          this.ComplianceProgress = data;

          this.reloadCOIGrid = true;

          this.displayAutoApproveOrExpiredPolicyModal();

          if (!this.ComplianceProgress) {
            this.SharedDataService.sendEvent(
              'UpdateComplianceProgress',
              new ViveProgress({ status: 'Reviewing', type: 'Insurance' })
            );
          } else {
            const { status } = this.ComplianceProgress;

            if (status === null) {
              this.SharedDataService.sendEvent(
                'UpdateComplianceProgress',
                new ViveProgress({ status: 'Reviewing', type: 'Insurance' })
              );
            }
          }
        }

        if (eventName === 'CheckForPassedFindings') {
          this.SharedDataService.sendEvent(
            'RespondIfPassedFindings',
            this.checkForPassedFindings()
          );
        }

        if (eventName === 'ReloadComplianceResults') {
          this.loadComplianceResults(this.CompanyId, this.VendorId).subscribe();
        }

        if (eventName === 'ReloadCompanyVendorData') {
          this.loadCompanyVendorData();
        }
      }
    });
  }

  SharedDataServiceSubscription!: Subscription;

  // We capture an instance of the Auto Approve modal to ensure we only display 1 at a time.
  AutoApproveModal?: NgbModalRef;

  pollingSubscription?: Subscription;
  timeoutTimer?: Observable<number>;
  pollingLoadingMask?: LoadingMask;

  CompanyId!: number;
  VendorId!: number;
  CompanyVendorId!: number;
  CompanyComplianceTypeId!: number;
  action!: 'review' | 'edit';

  CompanyVendor!: CompanyVendor;
  Vendor!: Vendor;
  Company!: Company;
  ComplianceResults!: GenericObject;
  CanadianWorkersCompensationFindingAndResults: {
    finding: string | null;
    result: string | null;
  }[] = [];
  InsuranceCertificates: InsuranceCertificate[] = [];
  InsuranceCertificatesMappedById = new Map<number, InsuranceCertificate>([]);
  ComplianceSetting!: ComplianceSetting;
  ComplianceProgress?: ViveProgress | null;

  SharedOperationStates = new Set<State>();

  // Maps Insurance Agents by id for quick updating.
  InsuranceAgentsMappedById = new Map<number, InsuranceAgent>([]);

  InsuranceComplianceResults?: {
    GL: GLInsuranceComplianceResults;
    AUTO: AUTOInsuranceComplianceResults;
    UMB: UMBInsuranceComplianceResults;
    WC: WCInsuranceComplianceResults | GenericObject;
    PL: PLInsuranceComplianceResults;
  };

  // This is used to determine Pass/Fail points for the Vive Named Cert Holder & Agent Signed Checkboxes
  // on the Certificate.
  InsuranceCertificateFindingResults?: {
    [key: number]: {
      viveNamedCertHolder: ComplianceResultStatuses;
      agentSigned: ComplianceResultStatuses;
    };
  };

  PolicyTypeToPolicyLabel: {
    GL: 'General Liability';
    AUTO: 'Automotive Liability';
    UMB: 'Umbrella Liability';
    WC: "Worker's Compensation";
    PL: 'Professional Liability';
  } = {
    GL: 'General Liability',
    AUTO: 'Automotive Liability',
    UMB: 'Umbrella Liability',
    WC: "Worker's Compensation",
    PL: 'Professional Liability'
  };

  InsuranceResultToLabelMap: {
    [key: string]: { label: string };
  } = {
    viveNamedCertHolder: {
      label: 'VIVE Named Cert Holder?'
    },
    agentSigned: {
      label: 'Agent Signed?'
    }
  };

  ComplianceResultToLabelMap = new InsuranceComplianceResultToLabelMap();

  InsuranceCertificateFormArray = new FormArray<InsuranceCertificateForm>([]);

  InsuranceCertificateStatuses: InsuranceCertificateStatus[] = [
    'Reviewing',
    'Accepted',
    'Rejected',
    'Archived',
    'Replaced',
    'Draft'
  ];

  SuppliedCOIGridQuery: GenericObject = {
    attributes: ['id', 'InsuranceAgentId', 'CompanyId', 'VendorId'],
    aliasedAttributes: ['policyExpirationDates'],
    status: { $notIn: ['Archived', 'Replaced'] },
    Vendor: {
      attributes: ['id', 'legalName']
    },
    Company: {
      attributes: ['id', 'legalName']
    }
  };

  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['edit', 'refresh', 'archive', 'delete']
  };

  reloadCOIGrid: boolean = false;

  COIGridFormGroup: FormGroup = this.FormBuilder.group({
    showArchived: [false]
  });

  LoggedInUser = this.AuthService.getUser();

  COITooltipMap: {
    [
      key in Extract<
        GridActionColumnActions,
        'edit' | 'refresh' | 'archive' | 'delete'
      >
    ]: string;
  } = {
    edit: 'Edit COI',
    refresh: 'Replace COI',
    archive: 'Archive COI',
    delete: 'Delete COI'
  };

  getCalculatedColumnActions: ({
    rowData
  }: {
    rowData: InsuranceCertificate;
  }) => GridActionColumnActions[] = ({
    rowData
  }: {
    rowData: InsuranceCertificate;
  }) => {
    const { status } = rowData;

    if (status === 'Archived') {
      return ['edit'];
    }

    return ['edit', 'refresh', 'archive', 'delete'];
  };

  coiGridAction({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: InsuranceCertificate;
  }) {
    const { id: InsuranceCertificateId } = rowData;

    if (action === 'edit') {
      this.editCOI(rowData, 'edit');
    }

    if (action === 'archive') {
      this.archiveCOI(rowData);
    }

    if (action === 'delete') {
      this.InsuranceService.deleteInsuranceCertificate({
        id: InsuranceCertificateId
      }).subscribe({
        next: () => {
          this.SharedDataService.sendEvent(
            'reloadComplianceProgress',
            new ViveProgress({ type: 'Insurance' })
          );
          this.reloadCOIGrid = true;
          this.ToastService.addSuccessToast(
            'Success',
            'Successfully deleted insurance certificate.'
          );
        }
      });
    }

    if (action === 'refresh') {
      this.editCOI(rowData, 'replace');
    }
  }

  castStringToNumber(value: string) {
    return Number(value);
  }

  castStringAsKeyofInsuranceCertificate(value: string) {
    return value as keyof InsuranceCertificate;
  }

  saveInsuranceCertificateStatus(
    InsuranceCertificateFormGroup: InsuranceCertificateForm
  ) {
    const {
      value: {
        id,
        CompanyVendorId,
        CompanyId,
        VendorId,
        InsuranceAgentId,
        status
      }
    } = InsuranceCertificateFormGroup;

    this.InsuranceService.saveInsuranceCertificate({
      id,
      CompanyVendorId,
      CompanyId,
      VendorId,
      InsuranceAgentId,
      status
    }).subscribe({
      next: () => {
        this.ToastService.addSuccessToast(
          'Success',
          'Successfully saved insurance certificate status.'
        );
      }
    });
  }

  getInsurancePoliciesOnCertificate(
    InsuranceCertificate: InsuranceCertificate
  ) {
    const { InsurancePolicies } = InsuranceCertificate;

    if (!InsurancePolicies) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Insurance Certificate is missing Insurance Policies.',
        { returnError: true }
      );
    }

    return InsurancePolicies.map((InsurancePolicy) => {
      const { policyType } = InsurancePolicy;

      if (!policyType) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Insurance Policy is missing policy types.',
          { returnError: true }
        );
      }

      return policyType;
    });
  }

  /**
   * CBRI-2136: field-level compliance findings can still read PASSED / NOT REQUIRED
   * when a required policy's expirationDate has lapsed (or status is Expired). Gate
   * auto-approve / "step is passing" on live policy expiration, not findings alone.
   *
   * VPDW-4205: the implementation now lives in `InsuranceExpirationService`, because the
   * vendor-level approve gate in `ComplianceReviewComponent` needs the same answer and
   * cannot reach this component (it does not exist yet when that gate first runs, and never
   * exists on the other four review routes). Two copies of this predicate is exactly what
   * put two contradictory modals on screen at once, so there is only one now.
   */
  hasExpiredRequiredInsurance(): boolean {
    return this.InsuranceExpirationService.hasExpiredRequiredInsurance();
  }

  checkForPassedFindings() {
    if (!this.CompanyVendor) {
      return false;
    }

    const { needsFeeWaiveRenewal } = this.CompanyVendor;

    if (needsFeeWaiveRenewal) {
      return false;
    }

    if (this.hasExpiredRequiredInsurance()) {
      return false;
    }

    const { country: clientCountry } = this.Company;

    if (
      this.InsuranceCertificateFindingResults &&
      this.InsuranceComplianceResults
    ) {
      if (clientCountry === 'CA') {
        this.InsuranceComplianceResults.WC = {
          canadianWorkersCompResults:
            this.validateCanadianWorkersCompensationResults()
        };
      }

      const InsurancePoliciesAndResults = Object.values(
        this.InsuranceComplianceResults
      );
      const InsuranceCertificateResults = Object.values(
        this.InsuranceCertificateFindingResults
      );
      return (
        InsurancePoliciesAndResults.length > 0 &&
        InsuranceCertificateResults.length > 0 &&
        InsurancePoliciesAndResults.every((InsurancePolicyResults) => {
          const Results = Object.values(
            InsurancePolicyResults
          ) as ComplianceResultStatuses[];
          return (
            Results.length > 0 &&
            Results.every((Result) => {
              return [
                'PASSED',
                'NOT REQUIRED',
                'EXEMPT',
                'EXEMPT - PASSED'
              ].includes(Result);
            })
          );
        }) &&
        InsuranceCertificateResults.every((CertificateResults) => {
          const Results = Object.values(
            CertificateResults
          ) as ComplianceResultStatuses[];
          return (
            Results.length > 0 &&
            Results.every((Result) => {
              return ['PASSED', 'NOT REQUIRED'].includes(Result);
            })
          );
        })
      );
    }

    return false;
  }

  displayAutoApproveOrExpiredPolicyModal() {
    if (this.hasExpiredRequiredInsurance()) {
      if (this.AutoApproveModal) {
        return;
      }

      this.AutoApproveModal = this.ModalService.open(ConfirmModalComponent, {
        backdrop: 'static',
        modalDialogClass: 'confirm-modal'
      });
      document.body.style.overflowY = 'auto';
      Object.assign(this.AutoApproveModal.componentInstance, {
        displayText: 'One or more required insurance policies have expired.',
        hideCancelButton: true
      });

      from(this.AutoApproveModal.result).subscribe({
        next: () => {
          delete this.AutoApproveModal;
        },
        error: () => {
          delete this.AutoApproveModal;
        }
      });
      return;
    }

    if (this.LoggedInUser && this.CompanyVendor) {
      const { UserAccountRole: { role } = {} } = this.LoggedInUser;
      const { viveStatus } = this.CompanyVendor;

      if (
        role &&
        new Set([
          'ADMIN_ADMIN',
          'ADMIN_INSURANCE_TEAM',
          'ADMIN_PROJECT_MANAGEMENT_TEAM'
        ]).has(role) &&
        this.checkForPassedFindings() &&
        (this.ComplianceProgress === null ||
          (this.ComplianceProgress &&
            !['Passed', 'Failed'].includes(this.ComplianceProgress.status!))) &&
        viveStatus !== 'Inactive' &&
        !this.AutoApproveModal
      ) {
        this.AutoApproveModal = this.ModalService.open(ConfirmModalComponent, {
          backdrop: 'static',
          modalDialogClass: 'confirm-modal'
        });
        document.body.style.overflowY = 'auto';
        Object.assign(this.AutoApproveModal.componentInstance, {
          // VPDW-4199: scoped to this step, and names any sibling step still needing
          // attention so this never reads as a review-wide all-clear.
          displayText:
            this.ComplianceStepStatusService.buildStepApprovalText('Insurance')
        });

        const { result } = this.AutoApproveModal;

        from(result).subscribe({
          next: (confirmed) => {
            if (confirmed) {
              this.SharedDataService.sendEvent(
                'UpdateComplianceProgress',
                new ViveProgress({ status: 'Passed', type: 'Insurance' })
              );
              // Final review no longer exists. Do not go to any next step.
              // this.SharedDataService.sendEvent('GoToNextStep');
            }
            delete this.AutoApproveModal;
          },
          error: () => {
            delete this.AutoApproveModal;
          }
        });
      }
    }
  }

  editCOI(
    InsuranceCertificate: InsuranceCertificate,
    action: 'edit' | 'replace'
  ) {
    const {
      id: InsuranceCertificateId,
      InsuranceAgentId,
      status
    } = InsuranceCertificate;

    if (!InsuranceCertificateId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Insurance Certificate is missing id.',
        { returnError: true }
      );
    }

    if (!InsuranceAgentId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Insurance Certificate is missing Insurance Agent Id.',
        { returnError: true }
      );
    }

    if (!new Set(['edit', 'replace']).has(action)) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing valid edit action.',
        { returnError: true }
      );
    }

    const NavArray = [
      'admin',
      'run-vive',
      'client-vendor',
      this.CompanyVendorId,
      'client',
      this.CompanyId,
      'vendor',
      this.VendorId,
      'compliance-type',
      this.CompanyComplianceTypeId,
      'action',
      this.action,
      'insurance-review',
      'insurance-agent',
      InsuranceAgentId,
      'coi'
    ];

    if (action === 'edit') {
      NavArray.push(InsuranceCertificateId);
    } else if (action === 'replace') {
      NavArray.push('replace', InsuranceCertificateId);
    }

    if (status === 'Created') {
      this.InsuranceService.saveInsuranceCertificate({
        id: InsuranceCertificateId,
        CompanyId: this.CompanyId,
        VendorId: this.VendorId,
        CompanyVendorId: this.CompanyVendorId,
        InsuranceAgentId,
        status: 'Draft'
      }).subscribe({
        next: () => {
          InsuranceCertificate.status = 'Draft';
          this.router.navigate(NavArray);
        }
      });
    } else {
      this.router.navigate(NavArray);
    }
  }

  archiveCOI(InsuranceCertificate: InsuranceCertificate, index?: number) {
    const { id } = InsuranceCertificate;
    const ConfirmModal = this.ModalService.open(ConfirmModalComponent, {
      backdrop: 'static',
      modalDialogClass: 'confirm-modal'
    });
    const { componentInstance, result } = ConfirmModal;
    Object.assign(componentInstance, {
      displayText:
        'Are you sure you want to archive this Insurance Certificate?'
    });

    from(result).subscribe({
      next: (confirm) => {
        if (confirm) {
          Object.assign(InsuranceCertificate, { status: 'Archived' });
          this.InsuranceService.saveInsuranceCertificate(
            InsuranceCertificate
          ).subscribe({
            next: (InsuranceCertificate) => {
              if (index) {
                this.InsuranceCertificates.splice(index, 1);
              } else {
                const InsuranceCertificateIndex =
                  this.InsuranceCertificates.findIndex(
                    (cert) => (cert.id = id)
                  );

                if (InsuranceCertificateIndex) {
                  this.InsuranceCertificates.splice(
                    InsuranceCertificateIndex,
                    1
                  );
                }
              }

              this.SharedDataService.sendEvent(
                'reloadComplianceProgress',
                new ViveProgress({ type: 'Insurance' })
              );
              this.reloadCOIGrid = true;
            }
          });
        }
      }
    });
  }

  getInsurancePolicyInfoFromInsuranceCertificate(
    InsuranceCertificate: InsuranceCertificate,
    InsurancePolicyField: keyof InsurancePolicy
  ) {
    const { InsurancePolicies } = InsuranceCertificate;

    if (!InsurancePolicies) {
      throw new Error(
        'An Insurance Certificate is missing Insurance Policies.'
      );
    }

    return InsurancePolicies.map((InsurancePolicy) => {
      const { [InsurancePolicyField]: field } = InsurancePolicy;

      if (!field) {
        throw new Error(
          `An Insurance Policy is missing ${InsurancePolicyField}.`
        );
      }

      return field;
    });
  }

  getInsurancePolicyTypesFromAgent(InsuranceAgent: InsuranceAgent) {
    const {
      isGLProvider,
      isAutoProvider,
      isUmbrellaProvider,
      isWorkersProvider,
      isProfessionalProvider
    } = InsuranceAgent;

    return [
      { providerType: isGLProvider, policyType: 'GL' },
      { providerType: isAutoProvider, policyType: 'AUTO' },
      { providerType: isUmbrellaProvider, policyType: 'UMB' },
      { providerType: isWorkersProvider, policyType: 'WC' },
      { providerType: isProfessionalProvider, policyType: 'PL' }
    ]
      .reduce((accum, providerPolicyObject) => {
        const { providerType, policyType } = providerPolicyObject;
        if (providerType) {
          accum.push(policyType);
        }
        return accum;
      }, [] as string[])
      .join(', ');
  }

  createTicket(InsuranceAgent: InsuranceAgent) {
    const CreateTicketModal = this.ModalService.open(
      CreateTicketModalComponent,
      { backdrop: 'static', modalDialogClass: 'create-ticket-modal' }
    );
    const { componentInstance } = CreateTicketModal;
    Object.assign(componentInstance, {
      CompanyVendor: this.CompanyVendor,
      CompanyId: this.CompanyId,
      VendorId: this.VendorId,
      InsuranceAgent,
      ticketType: 'newInsurance'
    });
    const observable = from(CreateTicketModal.result);
    observable.subscribe({
      next: (url) => {
        this.ToastService.addSuccessToast(
          'Success!',
          'Successfully created Zendesk Ticket.'
        );
        window.open(url, '_blank');
      }
    });
  }

  addCOI(InsuranceAgent: InsuranceAgent) {
    const {
      id: InsuranceAgentId,
      agencyName,
      firstName: agentFirstName,
      lastName: agentLastName,
      email: agentEmail
    } = InsuranceAgent;

    if (!InsuranceAgentId || !agentFirstName || !agentLastName || !agentEmail) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Insurance Agent information.',
        { returnError: true }
      );
    }

    const CoiUploadModal = this.ModalService.open(CoiUploadModalComponent, {
      backdrop: 'static',
      size: 'lg',
      modalDialogClass: 'coi-upload-modal'
    });

    const { componentInstance, closed } = CoiUploadModal;
    Object.assign(componentInstance, {
      InsuranceAgentName: agencyName
    });

    closed.subscribe({
      next: ({ scanType, COIFiles }) => {
        if (scanType && COIFiles.length === 1) {
          const [COIFile] = COIFiles;

          this.AdminService.sendCOIToBeScanned(COIFile, {
            CompanyId: this.CompanyId,
            VendorId: this.VendorId,
            CompanyVendorId: this.CompanyVendorId,
            InsuranceAgentId,
            scanType
          }).subscribe({
            next: (InsuranceCertificate: InsuranceCertificate) => {
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully sent COI to be scanned.'
              );

              if (!InsuranceCertificate.id) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'Missing Insurance Certificate Id.',
                  { returnError: true }
                );
              }

              this.startPollingForDraftCertificate(InsuranceCertificate.id);
              this.reloadCOIGrid = true;
            }
          });
        } else if (!scanType && COIFiles.length > 0) {
          this.router.navigate(
            [
              'admin',
              'run-vive',
              'client-vendor',
              this.CompanyVendorId,
              'client',
              this.CompanyId,
              'vendor',
              this.VendorId,
              'compliance-type',
              this.CompanyComplianceTypeId,
              'action',
              this.action,
              'insurance-review',
              'insurance-agent',
              InsuranceAgentId,
              'coi'
            ],
            {
              state: { uploadedFiles: COIFiles }
            }
          );
        } else {
          this.ToastService.addErrorToast(
            'Error',
            'No COI files were uploaded.'
          );
        }
      }
    });
  }

  archiveAgent(InsuranceAgent: InsuranceAgent) {
    const { id: InsuranceAgentId } = InsuranceAgent;

    if (!InsuranceAgentId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Insurance Agent Id.',
        { returnError: true }
      );
    }

    if (!this.VendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
        returnError: true
      });
    }

    const ConfirmModal = this.ModalService.open(ConfirmModalComponent, {
      backdrop: 'static',
      modalDialogClass: 'confirm-modal'
    });
    const { componentInstance, result } = ConfirmModal;

    // VPDW-4308: this used to promise the certificates would be "set to reviewing". They are
    // archived (VPDW-4289) — and before that fix the reviewing move was rejected outright by the
    // insurance-certificate status trigger, so the modal was describing an outcome that could not
    // happen. Archiving a COI is destructive, so it is confirmed as such.
    Object.assign(componentInstance, {
      title: 'Archive agent',
      displayText:
        'Archiving this agent will also archive the certificates of insurance it still holds, along with their policies. Certificates that were already replaced or archived are left as they are. This cannot be undone from this screen. Continue?',
      continueButtonText: 'Archive agent',
      confirmTone: 'danger'
    });
    from(result).subscribe({
      next: (response) => {
        if (response) {
          this.InsuranceService.archiveInsuranceAgent(InsuranceAgentId, {
            VendorId: this.VendorId
          }).subscribe({
            next: () => {
              this.InsuranceAgentsMappedById.delete(InsuranceAgentId);
              this.reloadCOIGrid = true;
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully archived agent.'
              );
            }
          });
        }
      }
    });
  }

  editAgent(InsuranceAgent: InsuranceAgent) {
    const { id: InsuranceAgentId } = InsuranceAgent;

    if (!InsuranceAgentId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Insurance Agent Id.',
        { returnError: true }
      );
    }

    const InsuranceAgentModal = this.ModalService.open(
      AddEditInsuranceAgentModalComponent,
      { backdrop: 'static', modalDialogClass: 'add-edit-insurance-agent-modal' }
    );
    const { componentInstance, result } = InsuranceAgentModal;
    Object.assign(componentInstance, {
      CompanyId: this.CompanyId,
      VendorId: this.VendorId,
      InsuranceAgentId
    });

    from(result).subscribe({
      next: (SavedInsuranceAgent) => {
        Object.assign(InsuranceAgent, SavedInsuranceAgent);
      }
    });
  }

  addAgent() {
    const InsuranceAgentModal = this.ModalService.open(
      AddEditInsuranceAgentModalComponent,
      { backdrop: 'static', modalDialogClass: 'add-edit-insurance-agent-modal' }
    );
    const { componentInstance, result } = InsuranceAgentModal;
    Object.assign(componentInstance, {
      CompanyId: this.CompanyId,
      VendorId: this.VendorId
    });

    from(result).subscribe({
      next: (SavedInsuranceAgent) => {
        const { id: InsuranceAgentId } = SavedInsuranceAgent;
        this.InsuranceAgentsMappedById.set(
          InsuranceAgentId,
          SavedInsuranceAgent
        );
      }
    });
  }

  startPollingForDraftCertificate(insuranceCertificateId: number) {
    if (!this.timeoutTimer) {
      const maxDuration = 90000;
      const pollInterval = 1000;
      this.timeoutTimer = timer(maxDuration);

      const { nativeElement } = this.HostElement;
      this.pollingLoadingMask = new LoadingMask(nativeElement, {
        maskText: 'Processing COI scan...'
      });

      this.pollingSubscription = interval(pollInterval)
        .pipe(
          takeUntil(this.timeoutTimer),
          switchMap(() =>
            this.InsuranceService.getInsuranceCertificate(
              {
                id: insuranceCertificateId,
                status: 'Draft'
              },
              { skipLoading: true }
            )
          )
        )
        .subscribe({
          next: (certificate: InsuranceCertificate | null) => {
            if (certificate && certificate.status === 'Draft') {
              this.handlePollingCompletion('completed');
            }
          },
          error: () => {
            this.handlePollingCompletion('timeout');
          },
          complete: () => {
            if (this.timeoutTimer) {
              this.handlePollingCompletion('timeout');
            }
          }
        });
    }
  }

  handlePollingCompletion(status: 'completed' | 'timeout') {
    this.timeoutTimer = undefined;
    this.pollingSubscription?.unsubscribe();

    if (this.pollingLoadingMask) {
      this.pollingLoadingMask.destroy();
      this.pollingLoadingMask = undefined;
    }

    if (status === 'completed') {
      this.ToastService.addSuccessToast(
        'Success',
        'COI has been processed and is ready for review.'
      );

      this.SharedDataService.sendEvent(
        'reloadComplianceProgress',
        new ViveProgress({ type: 'Insurance' })
      );

      this.reloadCOIGrid = true;
    } else if (status === 'timeout') {
      this.ToastService.addSuccessToast(
        'Info',
        'COI processing is taking longer than expected. Please refresh the page to see updates.'
      );
    }
  }

  isInsuranceCertificateFindingResults() {
    return !isEmpty(this.InsuranceCertificateFindingResults);
  }

  checkIfCertificateHasNextExpiringInsurancePolicyDate(
    InsuranceCertificateFormGroup: InsuranceCertificateForm
  ) {
    const {
      value: { id }
    } = InsuranceCertificateFormGroup;

    return (
      id &&
      this.InsuranceCertificatesMappedById.has(id) &&
      !!this.InsuranceCertificatesMappedById.get(id)!
        .nextExpiringInsurancePolicyDate
    );
  }

  loadComplianceResults(CompanyId: number, VendorId: number) {
    return this.ComplianceService.getComplianceResults(
      CompanyId,
      VendorId
    ).pipe(
      concatMap((ComplianceResults) => {
        if (!ComplianceResults) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Compliance results information.',
            { returnError: true }
          );
        }

        const { InsuranceCertificateFindingResults } = ComplianceResults;

        this.InsuranceCertificateFindingResults =
          InsuranceCertificateFindingResults;

        const GLResults: GLInsuranceComplianceResults =
          this.UtilityService.constructObjectFromObjectProperties(
            ComplianceResults,
            [
              'generalLiability_amBestRating',
              'generalLiability_financialSizeCategory',
              'generalLiability_policyAmountPerOccurance',
              'generalLiability_policyAmountAggregate',
              'generalLiability_productOps',
              'generalLiability_medicalExpenses',
              'generalLiability_damageToRentedPremises',
              'generalLiability_nonOwnedAutoAmount',
              'personalAdvertisementInjury',
              'glIsNamed',
              'glIsAgency',
              'glHiredAutos',
              'glNonOwnedAutos',
              'generalLiability_hasAiOngoingOps',
              'generalLiability_hasAiCompOps',
              'generalLiability_hasPnc',
              'generalLiability_hasPolicyDoc',
              'generalLiability_hasDescOps',
              'generalLiability_hasRequiredForms',
              'generalLiability_isContractualLiability',
              'generalLiability_isPerOccurrence',
              'glPerProjLocAgg',
              'glAdditionalInsuredEndorsements',
              'generalLiability_legalNameMismatch'
            ]
          );

        const AUTOResults: AUTOInsuranceComplianceResults =
          this.UtilityService.constructObjectFromObjectProperties(
            ComplianceResults,
            [
              'allowNonCommercialPolicy',
              'autoLiability_amBestRating',
              'autoLiability_financialSizeCategory',
              'autoLiability_combinedSingleLimit',
              'autoLiability_propertyDamageLimit',
              'autoLiability_bodilyInjuryPerPerson',
              'autoLiability_bodilyInjuryPerAccident',
              'autoIsNamed',
              'autoIsAgency',
              'autoHasNonOwnedAuto',
              'autoLiability_hasPolicyDoc',
              'autoLiability_hasDescOps',
              'autoLiability_hasRequiredForms',
              'autoAdditionalInsuredEndorsements',
              'autoLiability_legalNameMismatch'
            ]
          );

        const UMBResults: UMBInsuranceComplianceResults =
          this.UtilityService.constructObjectFromObjectProperties(
            ComplianceResults,
            [
              'umbrellaLiability_amBestRating',
              'umbrellaLiability_financialSizeCategory',
              'umbrellaLiability_policyAmountPerOccurance',
              'umbrellaLiability_policyAmountAggregate',
              'umbIsAgency',
              'umbIsNamed',
              'umbrellaLiability_hasPolicyDoc',
              'umbrellaLiability_hasDescOps',
              'umbrellaLiability_hasRequiredForms',
              'umbPrimaryNonContributory',
              'umbOccurrenceBased',
              'umbrellaLiability_legalNameMismatch'
            ]
          );

        const WCResults: WCInsuranceComplianceResults =
          this.UtilityService.constructObjectFromObjectProperties(
            ComplianceResults,
            [
              'workersCompensation_amBestRating',
              'workersCompensation_financialSizeCategory',
              'workersCompensation_eachIncidentEmployersLimit',
              'workersCompensation_eachEmployeeMinimum',
              'workersCompensation_eachEmployerMinimum',
              'wkIsAgency',
              'workersCompensation_hasDescOps',
              'workersCompensation_hasRequiredForms',
              'workersCompensation_officerExcluded',
              'workersCompensation_legalNameMismatch'
            ]
          );

        const PLResults: PLInsuranceComplianceResults =
          this.UtilityService.constructObjectFromObjectProperties(
            ComplianceResults,
            [
              'errorsAndOmissions_amBestRating',
              'errorsAndOmissions_financialSizeCategory',
              'errorsAndOmissions_policyAmount',
              'errIsNamed',
              'errIsAgency',
              'errorsAndOmissions_hasDescOps',
              'professionalLiability_legalNameMismatch'
            ]
          );

        this.InsuranceComplianceResults = {
          GL: GLResults,
          AUTO: AUTOResults,
          UMB: UMBResults,
          WC: WCResults,
          PL: PLResults
        };

        Object.assign(this, { ComplianceResults });

        return of(ComplianceResults);
      })
    );
  }

  getCanadianWorkersCompResults(
    CanadianWorkersCompensations: CanadianWorkersCompensation[],
    WorkerCompRequired: boolean
  ) {
    this.CanadianWorkersCompensationFindingAndResults.splice(0);
    CanadianWorkersCompensations.forEach((CWC) => {
      this.CanadianWorkersCompensationFindingAndResults.push(
        this.ComplianceService.calculateCanadianCompensationFindingAndResult(
          CWC,
          WorkerCompRequired
        )
      );
    });
  }

  validateCanadianWorkersCompensationResults() {
    const StatusMap = {
      'NOT REQUIRED': false,
      EXEMPT: false,
      MISSING: false,
      EXPIRED: false,
      FAILED: false,
      PASSED: false
    };

    this.CanadianWorkersCompensationFindingAndResults.forEach((cwcResult) => {
      const { result } = cwcResult;
      if (result) {
        StatusMap[result as keyof typeof StatusMap] = true;
      }
    });

    if (StatusMap['EXPIRED']) {
      return 'EXPIRED';
    } else if (StatusMap['FAILED']) {
      return 'FAILED';
    } else if (StatusMap['MISSING']) {
      return 'MISSING';
    } else if (StatusMap['EXEMPT']) {
      return 'EXEMPT';
    } else if (StatusMap['PASSED']) {
      return 'PASSED';
    } else {
      return 'NOT REQUIRED';
    }
  }

  loadCompanyVendorData() {
    forkJoin([
      this.CompanyVendorService.getCompanyVendorCount({
        CompanyId: this.CompanyId,
        VendorId: this.VendorId,
        isArchived: true
      }),
      this.ComplianceService.getClientVendorSharedStates(
        this.CompanyId,
        this.VendorId
      )
    ]).subscribe({
      next: ([
        countOfArchivedClientVendorRelationships,
        ClientVendorShatedStates
      ]) => {
        const ClientVendorSharedStatesSet = new Set(ClientVendorShatedStates);

        const CompanyVendorQuery: GenericObject = {
          attributes: ['id'],
          id: this.CompanyVendorId,
          Company: {
            attributes: ['id', 'legalName', 'hasGlobalCompliance', 'country'],
            ComplianceSetting: {
              options: { required: false },
              CompanyComplianceTypeId: {
                $col: 'CompanyVendor.CompanyComplianceTypeId'
              },
              literals: {
                id: { $eq: 'selectComplianceSettingIdForVendorRiskLevel' }
              }
            }
          },
          Vendor: {
            attributes: ['id', 'legalName'],
            aliasedAttributes: [
              'hasProfessionalTrade',
              'hasNonProfessionalTrade',
              'hasProfessionalOrNonProfessionalTrade'
            ],
            InsuranceAgent: {},
            ComplianceContact: {
              options: { required: false }
            }
          },
          InsuranceCertificate: {
            status: { $notIn: ['Archived', 'Replaced'] },
            aliasedAttributes: ['nextExpiringInsurancePolicyDate'],
            InsurancePolicy: {
              options: { required: true }
            }
          },
          CompanyVendorFile: {
            attributes: ['id', 'docType']
          }
        };

        if (countOfArchivedClientVendorRelationships > 0) {
          Object.assign(CompanyVendorQuery, { isArchived: true });
        }

        if (ClientVendorSharedStatesSet.has('ON')) {
          CompanyVendorQuery['WSIB'] = {};
        }

        if (ClientVendorSharedStatesSet.has('AB')) {
          CompanyVendorQuery['WCB'] = {};
        }

        forkJoin([
          this.CompanyVendorService.getCompanyVendor(
            this.UtilityService.stringifyObjectsInQueryObject(
              CompanyVendorQuery
            )
          ),
          this.loadComplianceResults(this.CompanyId, this.VendorId),
          // VPDW-4205: this method re-runs mid-session (a Canadian Workers' Comp edit sends
          // 'ReloadCompanyVendorData', and the review host reloads progress on save), and it
          // used to refresh the data behind this step's expired-policy modal. That data now
          // lives in the shared service, so refresh it here too — otherwise resolving a
          // lapse in-session would leave both the modal and the vendor-level approve gate
          // reading a stale page-load snapshot.
          this.InsuranceExpirationService.load(
            this.CompanyVendorId,
            this.CompanyId,
            this.VendorId
          )
        ]).subscribe({
          next: ([CompanyVendor, ComplianceResults]) => {
            if (!CompanyVendor) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Client/Vendor information.',
                { returnError: true }
              );
            }

            const {
              Company,
              Vendor,
              InsuranceCertificates,
              CompanyVendorFiles,
              WSIB,
              WCB
            } = CompanyVendor;

            if (Array.isArray(InsuranceCertificates)) {
              InsuranceCertificates.forEach((InsuranceCertificate) => {
                const { id: InsuranceCertificateId, InsurancePolicies } =
                  InsuranceCertificate;

                if (!InsuranceCertificateId) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Insurance Certificate is missing Id.',
                    { returnError: true }
                  );
                }

                if (
                  !Array.isArray(InsurancePolicies) ||
                  InsurancePolicies.length === 0
                ) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Insurance Policies.',
                    { returnError: true }
                  );
                }

                InsurancePolicies.forEach((InsurancePolicy) => {
                  const { policyType } = InsurancePolicy;

                  if (!policyType) {
                    throw this.ToastService.addErrorToast(
                      'Error',
                      'Missing policy type.',
                      { returnError: true }
                    );
                  }
                });

                this.InsuranceCertificates.push(InsuranceCertificate);
                this.InsuranceCertificatesMappedById.set(
                  InsuranceCertificateId,
                  InsuranceCertificate
                );

                const InsuranceCertificateFormGroup =
                  new InsuranceCertificateForm();

                InsuranceCertificateFormGroup.patchValue(InsuranceCertificate);
                this.InsuranceCertificateFormArray.push(
                  InsuranceCertificateFormGroup
                );
              });
            }

            if (CompanyVendorFiles && CompanyVendorFiles.length > 0) {
              const foundComplianceWaiverFile = CompanyVendorFiles.find(
                (file) => file.docType === 'Compliance Waiver'
              );

              if (foundComplianceWaiverFile) {
                this.ToastService.addSuccessToast(
                  'Info',
                  'Compliance Waiver on File.'
                );
              }
            }

            if (!Company) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Client information.',
                { returnError: true }
              );
            }

            const { ComplianceSettings, country: clientCountry } = Company;

            if (
              !Array.isArray(ComplianceSettings) ||
              ComplianceSettings.length === 0
            ) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Compliance Setting information.',
                { returnError: true }
              );
            }

            if (ComplianceSettings.length !== 1) {
              throw this.ToastService.addErrorToast(
                'Error',
                'There was an error loading Compliance Setting information.',
                { returnError: true }
              );
            }

            this.ComplianceSetting = ComplianceSettings[0];

            const CanadianWorkersCompensations: CanadianWorkersCompensation[] =
              [];

            if (WCB) {
              CanadianWorkersCompensations.push(WCB);
            }

            if (WSIB) {
              CanadianWorkersCompensations.push(WSIB);
            }

            if (clientCountry === 'CA') {
              if (CanadianWorkersCompensations.length > 0) {
                const WorkersCompRequired =
                  !!ComplianceSettings[0].workersCompensation_isRequired;

                if (this.InsuranceComplianceResults) {
                  this.getCanadianWorkersCompResults(
                    CanadianWorkersCompensations,
                    WorkersCompRequired
                  );

                  this.InsuranceComplianceResults.WC = {
                    canadianWorkersCompResults:
                      this.validateCanadianWorkersCompensationResults()
                  };
                }
              }
            }

            if (!CompanyVendor) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Client/Vendor information.',
                { returnError: true }
              );
            }

            this.CompanyVendor = CompanyVendor;

            if (!Vendor) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Vendor Information.',
                { returnError: true }
              );
            }

            const { InsuranceAgents } = Vendor;

            if (Array.isArray(InsuranceAgents)) {
              InsuranceAgents.forEach((InsuranceAgent) => {
                const { id: InsuranceAgentId } = InsuranceAgent;

                if (!InsuranceAgentId) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Insurance Agent is missing Id.',
                    { returnError: true }
                  );
                }

                this.InsuranceAgentsMappedById.set(
                  InsuranceAgentId,
                  InsuranceAgent
                );
              });
            }

            Object.assign(this, { Company, Vendor });

            this.SharedDataService.sendEvent('GetReviewAction');
            this.SharedDataService.sendEvent('GetSharedOperationStates');
          }
        });
      }
    });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe({
      next: (paramMap) => {
        const CompanyId = Number(paramMap.get('CompanyId'));
        const VendorId = Number(paramMap.get('VendorId'));
        const CompanyVendorId = Number(paramMap.get('CompanyVendorId'));
        const CompanyComplianceTypeId = Number(
          paramMap.get('CompanyComplianceTypeId')
        );
        const action = paramMap.get('action');

        if (!CompanyId) {
          throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
            returnError: true
          });
        }

        if (!VendorId) {
          throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
            returnError: true
          });
        }

        if (!CompanyVendorId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Client/Vendor Id.',
            { returnError: true }
          );
        }

        if (!CompanyComplianceTypeId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Compliance Type Id.',
            { returnError: true }
          );
        }

        if (!action) {
          throw this.ToastService.addErrorToast('Error', 'Missing action.', {
            returnError: true
          });
        }

        Object.assign(this, {
          CompanyId,
          VendorId,
          CompanyVendorId,
          CompanyComplianceTypeId,
          action
        });

        Object.assign(this.SuppliedCOIGridQuery, { CompanyId, VendorId });

        this.loadCompanyVendorData();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.SharedDataServiceSubscription) {
      this.SharedDataServiceSubscription.unsubscribe();
    }

    this.pollingSubscription?.unsubscribe();
    if (this.pollingLoadingMask) {
      this.pollingLoadingMask.destroy();
    }
  }
}
